import React, { useEffect } from 'react';
import _remove from 'lodash/remove';
import _orderBy from 'lodash/orderBy';
import Big from 'big.js';
import buildTradeId from 'helpers/buildTradeId';
import { NBXMarkets } from 'services';
import { useWSHook } from './useWSHook';

// TODO: when this is stable and how we want it, remove all the console.logs. Converts to warns where appropriate.

export default function useWSOrderBook({ pair, wsHook, onOrderClosed }) {
  const createdTradeIdsRef = React.useRef([]);
  const closedOrderIdsRef = React.useRef([]);
  const unmatchedTradesQueueRef = React.useRef([]);
  const latestOrderOpenedRef = React.useRef(null);
  const onOrderClosedRef = React.useRef(onOrderClosed);

  useEffect(() => {
    onOrderClosedRef.current = onOrderClosed;
  }, [onOrderClosed]);

  const rawDataUniqBy = 'id';
  const fetchInitDataToRawData = React.useCallback(
    async () => NBXMarkets.getMarketOrders(pair, true),
    [pair]
  );

  const processWSEventDataToRawData = React.useCallback((eventData, currentRawData, outOfOrderEventsData) => {
    const newRawData = [...currentRawData];
    const newOutOfOrderEvents = [...outOfOrderEventsData];

    switch (eventData.type) {
      case 'ORDER-CLOSED': {
        const order = eventData.data;
        const closedOrders = _remove(newRawData, o => o.id === order.id);
        // If there is no matching order on the book, we probably got this event out of order
        // We then push it to a list of out of order events, to await the corresponding ORDER-OPENED event
        if (closedOrders.length === 0) {
          newOutOfOrderEvents.push(order);
        } else {
          // else push to a list of successfully closed orders
          closedOrderIdsRef.current.push(order.id);
        }
        onOrderClosedRef?.current && onOrderClosedRef.current(order.id);
        break;
      }
      case 'ORDER-OPENED': {
        const order = eventData.data;
        const matchingOutOfOrderEventIndex = newOutOfOrderEvents.findIndex(outOfOrderEventData => {
          return outOfOrderEventData.id === order.id;
        });

        if (matchingOutOfOrderEventIndex === -1) {
          // If order events were received in the correct order (ORDER-OPENED first), push the order to the raw data list of open orders
          newRawData.unshift(order);
          latestOrderOpenedRef.current = order;
        } else {
          // If order was already closed (out of order events), we add the order directly to the list of closed orders
          // and remove the entry in the out of order events list
          newOutOfOrderEvents.splice(matchingOutOfOrderEventIndex, 1);
          closedOrderIdsRef.current.push(order.id);
        }

        // Check if we've already received a TRADE-CREATED event for this order (out of order events)
        const tradeAwaitingForThisOrderIndex = unmatchedTradesQueueRef.current.findIndex(
          trade => trade.maker.id === order.id || trade.taker.id === order.id
        );

        // If we have received a TRADE-CREATED event, remove the trade from the list of "unmatched" trades
        // and process that trade so it can be displayed
        if (tradeAwaitingForThisOrderIndex !== -1) {
          const awaitingTrade = unmatchedTradesQueueRef.current[tradeAwaitingForThisOrderIndex];
          // eslint-disable-next-line
          console.log(
            `received order that has already been matched ${order.id}, trade item is`,
            awaitingTrade
          );
          return processWSEventDataToRawData(
            {
              type: 'TRADE-CREATED',
              data: awaitingTrade
            },
            newRawData,
            newOutOfOrderEvents
          );
        }
        break;
      }
      case 'TRADE-CREATED': {
        const trade = eventData.data;
        console.log('trade created', trade); // eslint-disable-line

        const tradeId = buildTradeId(trade.maker.id, trade.taker.id);

        if (createdTradeIdsRef.current.includes(tradeId)) break; // protection from duplicate trade-created

        const makerOrderExistedOrStillExists =
          closedOrderIdsRef.current.includes(trade.maker.id) ||
          newRawData.find(_order => _order.id === trade.maker.id);
        const takerOrderExistedOrStillExists =
          closedOrderIdsRef.current.includes(trade.taker.id) ||
          newRawData.find(_order => _order.id === trade.taker.id);

        const unmatchedTradeQueueIndex = unmatchedTradesQueueRef.current.findIndex(checkingTrade => {
          const checkingTradeId = buildTradeId(checkingTrade);
          return checkingTradeId === tradeId;
        });
        const tradeIsComingFromQueue = unmatchedTradeQueueIndex !== -1;

        if ((!makerOrderExistedOrStillExists || !takerOrderExistedOrStillExists) && !tradeIsComingFromQueue) {
          console.log('didnt find maker or taker trade, pushing to queue'); // eslint-disable-line
          // we can't match orders, because either maker or taker order never existed in front-end.
          // also make sure we are not pushing the trade into queue more than once.
          unmatchedTradesQueueRef.current.push(trade);
          break;
        }

        createdTradeIdsRef.current.push(tradeId);
        if (tradeIsComingFromQueue) {
          console.log('trade was coming from queue'); // eslint-disable-line
          // trade was coming from queue, therefore remove
          unmatchedTradesQueueRef.current.splice(unmatchedTradeQueueIndex, 1);
        }

        if (createdTradeIdsRef.current.length > 500) {
          // mem leak protection. Only keep last 500 trade IDs
          createdTradeIdsRef.current = createdTradeIdsRef.current.slice(-500);
        }
        if (closedOrderIdsRef.current.length > 500) {
          // mem leak protection. Only keep last 500 order ids
          closedOrderIdsRef.current = closedOrderIdsRef.current.slice(-500);
        }
        if (unmatchedTradesQueueRef.current.length > 500) {
          // mem leak protection. Only keep last 500 unmatched trades
          unmatchedTradesQueueRef.current = unmatchedTradesQueueRef.current.slice(-500);
        }

        const makerOrderIndex = newRawData.findIndex(order => order.id === trade.maker.id);
        const takerOrderIndex = newRawData.findIndex(order => order.id === trade.taker.id);
        const makerOrder = makerOrderIndex !== -1 ? newRawData[makerOrderIndex] : null;
        const takerOrder = makerOrderIndex !== -1 ? newRawData[takerOrderIndex] : null;

        if (makerOrder) {
          console.log('maker order found'); // eslint-disable-line
          if (makerOrder.quantity - trade.quantity <= 0) {
            console.warn(
              `Maker order: ${makerOrder.id} after update would have quantity: ${
                makerOrder.quantity - trade.quantity
              }`
            );
            newRawData.splice(makerOrderIndex, 1);
          } else {
            console.log('reducing maker quantity'); // eslint-disable-line
            makerOrder.quantity -= trade.quantity;
          }
        }

        if (takerOrder) {
          console.log('taker order found'); // eslint-disable-line
          if (takerOrder.quantity - trade.quantity <= 0) {
            console.warn(
              `Taker order: ${takerOrder.id} after update would have quantity: ${
                takerOrder.quantity - trade.quantity
              }`
            );
            newRawData.splice(takerOrderIndex, 1);
          } else {
            console.log('reducing taker quantity'); // eslint-disable-line
            takerOrder.quantity -= trade.quantity;
          }
        }

        break;
      }
      default: {
        console.warn(`Invalid event type: ${eventData.type} received from WS event.`);
        break;
      }
    }
    return {
      newRawData,
      newOutOfOrderEvents
    };
  }, []);

  const processRawData = React.useCallback(rawData => {
    let buys = [];
    let sells = [];
    const buysPriceMap = {};
    const sellsPriceMap = {};
    let maxBuyAmount = '0';
    let maxSellAmount = '0';
    let lowestSellPrice = Number.MAX_SAFE_INTEGER;
    let highestBuyPrice = 0;
    rawData.forEach(order => {
      if (order.side === 'BUY') {
        if (buysPriceMap[order.price] !== undefined) {
          const existingOrder = buys[buysPriceMap[order.price]];
          existingOrder.quantity = Big(existingOrder.quantity).plus(order.quantity).toFixed();
          buys[buysPriceMap[order.price]] = existingOrder;
        } else {
          if (Big(order.quantity).gt(maxBuyAmount)) maxBuyAmount = order.quantity;
          if (Big(order.price).gt(highestBuyPrice)) highestBuyPrice = order.price;
          buysPriceMap[order.price] = buys.length;
          buys.push({ price: order.price, quantity: order.quantity });
        }
      }
      if (order.side === 'SELL') {
        if (sellsPriceMap[order.price] !== undefined) {
          const existingOrder = sells[sellsPriceMap[order.price]];
          existingOrder.quantity = Big(existingOrder.quantity).plus(order.quantity).toFixed();
          sells[sellsPriceMap[order.price]] = existingOrder;
        } else {
          if (Big(order.quantity).gt(maxSellAmount)) maxSellAmount = order.quantity;
          if (Big(order.price).lt(lowestSellPrice)) lowestSellPrice = order.price;
          sellsPriceMap[order.price] = sells.length;
          sells.push({ price: order.price, quantity: order.quantity });
        }
      }
    });

    function calculateSpread() {
      return parseFloat(Big(lowestSellPrice).minus(highestBuyPrice), 10);
    }

    sells = _orderBy(sells, [sell => +sell.price], ['desc']);
    buys = _orderBy(buys, [buy => +buy.price], ['desc']);
    let spread = calculateSpread();

    // If a new order creates negative spread, we just remove it from the order book since it will be matched in a moment anyway
    // Prevents a flash of negative spread every time somebody makes a Quick Buy order.
    if (spread < 0) {
      console.warn('Negative spread detected, removing offending order since it will be matched soon anyway');
      const offendingPrice = latestOrderOpenedRef.current.price;
      sells = sells.filter(sell => sell.price !== offendingPrice);
      buys = buys.filter(buy => buy.price !== offendingPrice);
      lowestSellPrice = sells[sells.length - 1].price;
      highestBuyPrice = buys[0].price;
      spread = calculateSpread();
    }

    return {
      buys,
      sells,
      spread,
      maxBuyAmount,
      maxSellAmount
    };
  }, []);

  return useWSHook({
    wsHook,
    rawDataUniqBy,
    processRawData,
    processWSEventDataToRawData,
    fetchInitDataToRawData,
    initialProcessedDataState: {}
  });
}
